import {
  CustomerSubscriptionSchema,
  WashPackageSchema,
  type CustomerSubscription,
} from '@kilat/shared';
import { collection, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';

import { db } from '../firebase.js';
import { createRepo } from './_base.js';

/**
 * Sprint 92 — Wash subscription packages.
 *
 * Sprint 103 — migrated to the tenant-library pattern:
 *
 *   tenants/{tid}/washPackages/{packageId} → operator-defined templates,
 *     tenant-scoped (NOT per-outlet). Each item carries
 *     `excludedOutletIds: string[]` (default `[]` = available at every
 *     outlet). Public read so customer-pwa can render purchase lists;
 *     owner/manager writes via firestore.rules. See
 *     [[project-tenant-library-catalog]] + `docs/architecture/tenant-library.md`.
 *
 *   tenants/{tid}/subscriptions/{subscriptionId} → customer subscription
 *     instances. Unchanged from Sprint 92 — already tenant-scoped.
 *     Operator staff read access via firestore.rules; writes are
 *     server-only (callables — issueWashSubscription, redeem, cancel).
 *
 * Sprint 96 added `subscriptionsByCustomerSnapshot` — a filtered query
 * so the /customers detail panel + /pos redemption affordance can
 * scope their listener to ONE customer, not the whole tenant collection.
 */

export function washPackagesRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/washPackages`,
    schema: WashPackageSchema,
    name: `tenants/${tenantId}/washPackages`,
  });
}

export function tenantSubscriptionsRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/subscriptions`,
    schema: CustomerSubscriptionSchema,
    name: `tenants/${tenantId}/subscriptions`,
  });
}

/**
 * Live subscription list for ONE customer in ONE tenant. Filters
 * server-side via `where('customerUid', '==', customerUid)` so the
 * dashboard never reads the whole `tenants/{tid}/subscriptions`
 * collection just to render a single customer panel — matters once
 * an operator has hundreds of pack-holders.
 *
 * Skips per-doc schema-validation failures rather than dropping the
 * whole subscription (mirrors `createRepo.listSnapshot`).
 *
 * Passing `onError` is STRONGLY recommended: without it, a Firestore
 * permission failure or transient network error leaves the listener
 * silently stalled and the caller stuck on "loading…" forever — a
 * dead-end under [[feedback-no-dead-ends]]. The dashboard wires this
 * into a friendly "Couldn't load…" + retry banner.
 */
export function subscriptionsByCustomerSnapshot(
  tenantId: string,
  customerUid: string,
  cb: (subs: CustomerSubscription[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const ref = collection(db, `tenants/${tenantId}/subscriptions`);
  const q = query(ref, where('customerUid', '==', customerUid));
  return onSnapshot(
    q,
    (snap) => {
      const out: CustomerSubscription[] = [];
      for (const d of snap.docs) {
        const parsed = CustomerSubscriptionSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) out.push(parsed.data);
        // Bad-shape docs dropped silently — same policy as createRepo.listSnapshot.
      }
      cb(out);
    },
    (err) => {
      // Firestore-level listener error (permission, network, quota). Pass
      // to caller so the UI can surface it instead of hanging in "loading".
      if (onError) onError(err);
    },
  );
}
